import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowsLeftRight, MagnifyingGlass, Lightning, ArrowRight, Trophy, TrendUp } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO, { BreadcrumbLd, FaqLd } from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";

const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24", PICK: "#ccff00" };

function PosBadge({ pos }) {
  if (pos === "PICK") return <span className="text-[9px] px-1.5 py-0 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span>;
  return <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm pos-bg-${(pos||"").toLowerCase()} pos-${(pos||"").toLowerCase()}`}>{pos}</span>;
}

function PlayerThumb({ id, size = 24 }) {
  if (!id) return null;
  return (
    <div className="rounded-full overflow-hidden bg-[#262626] shrink-0" style={{ width: size, height: size }}>
      <img src={`https://sleepercdn.com/content/nfl/players/thumb/${id}.jpg`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} loading="lazy" />
    </div>
  );
}

function TradeCard({ trade, highlight }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-3 sm:p-4" data-testid="trade-card">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[9px] text-[#555] uppercase tracking-wider">Week {trade.week || "?"}</p>
        {highlight && <p className="text-[9px] text-[#ccff00] uppercase tracking-wider">Involving {highlight}</p>}
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1.5">
          <p className="text-[8px] text-[#555] uppercase tracking-wider font-bold">SIDE A GETS</p>
          {(trade.side_a || []).map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position !== "PICK" && <PlayerThumb id={p.player_id || p.sleeperId} size={22} />}
              <PosBadge pos={p.position} />
              {p.position !== "PICK" ? (
                <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
              ) : (
                <span className="text-xs text-white">{p.name}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center self-center px-2"><ArrowsLeftRight size={14} className="text-[#444]" /></div>
        <div className="flex-1 space-y-1.5">
          <p className="text-[8px] text-[#555] uppercase tracking-wider font-bold">SIDE B GETS</p>
          {(trade.side_b || []).map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position !== "PICK" && <PlayerThumb id={p.player_id || p.sleeperId} size={22} />}
              <PosBadge pos={p.position} />
              {p.position !== "PICK" ? (
                <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
              ) : (
                <span className="text-xs text-white">{p.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ Trade Explorer Landing Page ============ */
export function TradeExplorer() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trades/popular`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const searchPlayers = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/players/search?q=${encodeURIComponent(q)}&numQbs=2&numTeams=12&ppr=1`);
      setSuggestions((res.data || []).slice(0, 8));
      setShowSuggestions(true);
    } catch { setSuggestions([]); }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPlayers(val), 250);
  };

  const handleSelect = (player) => {
    const slug = player.name.toLowerCase().replace(/[.']/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
    setShowSuggestions(false);
    setSearch("");
    navigate(`/trades/${slug}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSelect(suggestions[0]);
    } else if (search.trim()) {
      const slug = search.trim().toLowerCase().replace(/[.']/g, "").replace(/\s+/g, "-");
      navigate(`/trades/${slug}`);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a]"><PublicNav /><div className="max-w-5xl mx-auto px-4 py-10"><div className="h-10 w-64 skeleton rounded-sm mb-4" /><div className="h-48 skeleton rounded-sm" /></div></div>;

  const popular = data?.popular_players || [];
  const samples = data?.sample_trades || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Trade Database: 40,000+ Real Trades from 6,976 Sleeper Leagues — Dynasty Trade Maker" description="Browse real dynasty trades from 6,976 Sleeper leagues. See what players actually traded for — not estimates. Search any player to see their real trade market." path="/trades" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">{(data?.total_trades || 0).toLocaleString()} REAL TRADES FROM {(data?.total_leagues || 0).toLocaleString()} LEAGUES</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mb-3">DYNASTY <span className="text-[#ccff00]">TRADE DATABASE</span></h1>
          <p className="text-base text-[#a3a3a3] max-w-2xl mx-auto mb-6">Real trades from real Sleeper dynasty leagues. See what players are actually being traded for — not calculator estimates, not crowd rankings. Actual completed deals.</p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto relative" ref={searchRef}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                <input type="text" value={search} onChange={handleSearchChange} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} placeholder="Search player (e.g. Bijan Robinson)" className="w-full h-11 pl-10 pr-4 bg-[#171717] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none" data-testid="trade-search-input" autoComplete="off" />
              </div>
              <button type="submit" className="h-11 px-5 btn-primary rounded-sm text-xs font-bold uppercase" data-testid="trade-search-btn">SEARCH</button>
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#171717] border border-[#262626] rounded-sm z-50 shadow-lg max-h-64 overflow-y-auto" data-testid="search-suggestions">
                {suggestions.map((p, i) => (
                  <button key={i} onClick={() => handleSelect(p)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-[#262626] transition-colors border-b border-[#1a1a1a] last:border-0" data-testid={`suggestion-${i}`}>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm pos-bg-${(p.position||"").toLowerCase()} pos-${(p.position||"").toLowerCase()}`}>{p.position}</span>
                    <span className="text-sm text-white flex-1">{p.name}</span>
                    <span className="text-[10px] text-[#555]">{p.team}</span>
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>

        {/* Most Traded Players */}
        <div className="mb-8">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><TrendUp size={12} /> MOST TRADED PLAYERS</h2>
          <div className="border border-[#262626] overflow-hidden">
            <div className="grid grid-cols-5 bg-[#0a0a0a] px-3 py-2 border-b border-[#262626] text-[9px] font-bold uppercase tracking-wider text-[#555]">
              <span>#</span><span className="col-span-2">Player</span><span>Pos</span><span className="text-right">Trades</span>
            </div>
            {popular.slice(0, 20).map((p, i) => (
              <Link key={i} to={`/trades/${p.slug}`} className="grid grid-cols-5 px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors items-center">
                <span className="text-xs text-[#555]">{i + 1}</span>
                <span className="text-xs text-white col-span-2 flex items-center gap-2">
                  <PlayerThumb id={p.sleeperId || p.player_id} size={22} />
                  {p.name}
                </span>
                <span className="text-xs" style={{ color: PC[p.position] }}>{p.position}</span>
                <span className="text-xs font-mono text-[#ccff00] text-right">{p.trade_count}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Sample Trades */}
        {samples.length > 0 && (
          <div className="mb-8">
            <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><ArrowsLeftRight size={12} /> RECENT TRADES</h2>
            <div className="space-y-3">
              {samples.slice(0, 6).map((t, i) => <TradeCard key={i} trade={t} highlight={t.featuring} />)}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center mb-6">
          <h3 className="font-heading text-lg text-white uppercase mb-2">USE THESE TRADES TO INFORM YOURS</h3>
          <p className="text-xs text-[#888] mb-4">Import your Sleeper league to get AI-powered trade suggestions based on real market data.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold"><Lightning size={12} weight="fill" /> IMPORT YOUR LEAGUE</a>
            <Link to="/trade-calculator" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Trade Calculator <ArrowRight size={11} /></Link>
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/research" className="hover:text-[#a3a3a3] no-underline">Research</Link>
        </div>
      </div>
    </div>
  );
}

/* ============ Per-Player Trade Page ============ */
export function PlayerTrades() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const fetchTrades = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trades/player/${slug}?limit=${LIMIT}&offset=${offset}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [slug]);

  useEffect(() => { fetchTrades(page * LIMIT); }, [page, fetchTrades]);

  if (loading && !data) return <div className="min-h-screen bg-[#0a0a0a]"><PublicNav /><div className="max-w-4xl mx-auto px-4 py-10"><div className="h-10 w-64 skeleton rounded-sm mb-4" /><div className="h-48 skeleton rounded-sm" /></div></div>;

  if (!data || data.total_trades === 0) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-[#a3a3a3] text-lg mb-4">No trades found for this player</p>
        <Link to="/trades" className="text-[#ccff00] text-sm no-underline hover:underline">Browse all trades</Link>
      </div>
    </div>
  );

  const { player, total_trades, trades } = data;
  const totalPages = Math.ceil(total_trades / LIMIT);
  const tradeFaqs = [
    { q: `What is ${player} being traded for in dynasty leagues?`, a: `This page tracks ${total_trades} real completed trades involving ${player} from Sleeper dynasty leagues — actual deals managers accepted, not calculator estimates. Browse the trades above to see the real market.` },
    { q: `How many real dynasty trades involve ${player}?`, a: `Dynasty Trade Maker has tracked ${total_trades} completed trades involving ${player}, sourced from thousands of real Sleeper dynasty leagues and updated as new trades happen.` },
    { q: `How should I value ${player} in a dynasty trade?`, a: `Start with the real market on this page, check ${player}'s live consensus value on the player profile, then test your exact package in the free trade calculator before you send the offer.` },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={`${player} Dynasty Trades: ${total_trades} Real Deals from Sleeper Leagues — Dynasty Trade Maker`} description={`See all ${total_trades} real dynasty trades involving ${player} from actual Sleeper leagues. Find out what ${player} is being traded for right now.`} path={`/trades/${slug}`} />
      <BreadcrumbLd items={[{ name: "Home", url: "/" }, { name: "Trade Database", url: "/trades" }, { name: `${player} Trades`, url: `/trades/${slug}` }]} />
      <FaqLd faqs={tradeFaqs} />
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/trades" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Trade Database</Link>

        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-white uppercase mt-4 mb-2" data-testid="player-trades-title">{player} <span className="text-[#ccff00]">TRADES</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-6"><strong className="text-white">{total_trades}</strong> real trades involving {player} from Sleeper dynasty leagues.</p>

        {/* Player links */}
        <div className="flex gap-2 mb-6">
          <Link to={`/players/${slug}`} className="text-[10px] px-3 py-1.5 border border-[#ccff00]/30 text-[#ccff00] no-underline rounded-sm hover:bg-[#ccff00]/10">{player} Profile</Link>
          <Link to="/trade-calculator" className="text-[10px] px-3 py-1.5 border border-[#262626] text-[#a3a3a3] no-underline rounded-sm hover:border-[#444]">Trade Calculator</Link>
        </div>

        {/* Trades list */}
        <div className="space-y-3 mb-6">
          {trades.map((t, i) => <TradeCard key={i} trade={t} />)}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] rounded-sm hover:border-[#444] disabled:opacity-30">Prev</button>
            <span className="text-xs text-[#555]">Page {page + 1} of {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="px-3 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] rounded-sm hover:border-[#444] disabled:opacity-30">Next</button>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-[#1a1a1a]" data-testid="trade-faq-section">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4 mb-8">
            {tradeFaqs.map((f, i) => (
              <div key={i}>
                <p className="text-xs font-bold text-white mb-1">{f.q}</p>
                <p className="text-xs text-[#888] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">All Trades</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
